import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipListbox, MatChipSelectionChange, MatChipsModule } from '@angular/material/chips';
import _ from 'lodash';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { environment } from 'src/environments/environment';
import { fadeInOutWithBlur, slideDownUp } from '../global-app-animations';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';


interface CameraDevice { deviceId: string; label: string; }
@Component({
  selector: 'app-camera',
  standalone: true,
  imports: [CommonModule, FormsModule, MatChipsModule, ReactiveFormsModule, MatSlideToggleModule],
  templateUrl: './camera.component.html',
  styleUrl: './camera.component.scss',
  animations: [slideDownUp, fadeInOutWithBlur]
})
export class CameraComponent implements OnInit, OnDestroy {
  @ViewChild('videoEl', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('cropBox', { static: true }) cropBoxRef!: ElementRef<HTMLElement>;
  @ViewChild('chipList') chipList!: MatChipListbox;
  @Input() showSettings: boolean = false;

  @Output() onCapture: EventEmitter<string> = new EventEmitter<string>();
  private imageCapture!: any;

  cameras: CameraDevice[] = [];
  selected = '';
  private stream: MediaStream | null = null;
  showFreezeHint = false;
  cameraLoading: boolean = true;
  cameraError: boolean = false;
  showFrame: boolean = true;
  ocrType: boolean = false; //true uses easyocr

  form = new FormGroup({
    camera: new FormControl<string>('', Validators.required),
    frame: new FormControl<boolean>(this.showFrame, Validators.required),
    ocrType: new FormControl<boolean>(this.ocrType, Validators.required)
  });

  cropWidth = 90;  // in % of video element
  cropHeight = 10;  // in % of video element


  constructor(private localStorageService: LocalStorageService) { }



  @HostListener('window:resize')
  onWindowResize() {
    this.resizeVideoToPortrait1080x1440();
  }

  private resizeVideoToPortrait1080x1440() {
    const video = document.getElementById('camPreviewContainer');
    if (!video) return;

    // target “design” resolution
    const DESIGN_W = 1080;
    const DESIGN_H = 1440;

    // actual viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // scale so the DESIGN box fits inside the viewport
    const scale = Math.min(vw / DESIGN_W, vh / DESIGN_H);

    const cssW = DESIGN_W * scale;
    const cssH = DESIGN_H * scale;

    // apply CSS size
    video.style.width = `${cssW}px`;
    video.style.height = `${cssH}px`;
  }


  /* ---------- lifecycle ---------- */
  async ngOnInit() {
    await this.askPermission();
    await this.listCameras();
    if (this.cameras.length > 0) {

      let frameCookie = this.localStorageService.getCookie(environment.Cookies.showFrame);
      if (frameCookie) {
        this.showFrame = frameCookie == 'true';
        this.form.controls['frame'].setValue(this.showFrame);

      }

      let ocrCookie = this.localStorageService.getCookie(environment.Cookies.ocrType);
      if (ocrCookie) {
        this.ocrType = ocrCookie == 'true';
        this.form.controls['ocrType'].setValue(this.ocrType);

      }

      let cameraCookie = this.localStorageService.getCookie(environment.Cookies.preferedCamera);
      if (cameraCookie) {

        this.selected = cameraCookie;
        const currentCamera = _.cloneDeep(this.cameras.find(x => x.deviceId == this.selected))
        if (currentCamera) {

          this.cameras.splice(this.cameras.findIndex(x => x.deviceId == this.selected), 1)
          this.cameras.unshift(currentCamera)
        }
      } else {
        this.selected = this.cameras[0].deviceId;
      }
      this.form.get('camera')!.setValue(this.selected);

      await this.openHighest(this.selected);
    }
  }
  ngOnDestroy() { this.stop(); }

  /* ---------- public ---------- */
  switchCam(event: MatChipSelectionChange, id: string) {
    if (!event.selected) {
      // re-select that chip:
      this.form.controls['camera'].setValue(id);
      event.source.select();
    } else if (id != this.selected) {

      this.selected = id;
      this.localStorageService.setCookie(environment.Cookies.preferedCamera, this.selected);
      this.openHighest(id);
    }

  }

  changeFrameVisibility(event: MatSlideToggleChange) {
    this.showFrame = event.checked;
    this.localStorageService.setCookie(environment.Cookies.showFrame, this.showFrame);
  }
  
  changeOcrType(event: MatSlideToggleChange) {
    this.ocrType = event.checked;
    this.localStorageService.setCookie(environment.Cookies.ocrType, this.ocrType);
  }

  retry() { if (this.selected) this.openHighest(this.selected); }



  /* ---------- private helpers ---------- */
  private async askPermission() {
    const tmp = await navigator.mediaDevices.getUserMedia({ video: true });
    tmp.getTracks().forEach(t => t.stop());
  }

  /* ---------- listCameras() rivisitato ---------- */
  private async listCameras() {
    const devs = await navigator.mediaDevices.enumerateDevices();
    const cams = devs.filter(d => d.kind === 'videoinput');

    // iOS ha già label corrette → usiamo le sue
    if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
      this.cameras = cams.filter(x => !x.label.toLowerCase().includes('front') && !x.label.toLowerCase().includes('anteriore') ).map((d, i) => ({
        deviceId: d.deviceId,
        label: d.label || `Fotocamera ${i + 1}`
      }));
      return;
    }

    // Android / desktop: costruiamo label dedotte
    this.cameras = await Promise.all(cams.filter(x => !x.label.toLowerCase().includes('front') && !x.label.toLowerCase().includes('anteriore') ).map((d, i) => ({
      deviceId: d.deviceId, label: `Fotocamera ${i + 1}`
    })
    ));
  }


  /**
   * Always tries to reach the real max resolution of the track
   */
  private async openHighest(deviceId: string) {
    this.cameraLoading = true;
    this.stop();

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    // iOS <17.5 ignores >1080p, but still ask high (no harm)
    const baseHi = {
      width: { ideal: 1440, max: 1440 },
      height: { ideal: 1080, max: 1080 },
      frameRate: { ideal: 20, max: 20 },
      audio: false,
      ...({ resizeMode: 'none' } as any)
    };

    // 1st attempt
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, ...baseHi }
      });
    } catch (e: any) {
      this.stop();
      if (e.name === 'OverconstrainedError') {
        // retry at 1080p
        try {
          this.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: deviceId },
              width: { ideal: 1080, max: 1080 },
              height: { ideal: 1920, max: 1920 }
            }, audio: false
          });
        } catch {
          // last fallback: generic back camera
          this.stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { exact: 'environment' } }, audio: false
          });
        }
      } else {
        this.cameraError = true;
        return;
      }; //TODO: add fallback message when no camera access
    }

    // 2nd step – push to real max via applyConstraints
       /* try {
      const track = this.stream.getVideoTracks()[0];
        const caps = track.getCapabilities();
  
        const hi: MediaTrackConstraints = {
          width: caps.width?.max,
          height: caps.height?.max,
          frameRate: caps.frameRate?.max
        };
        if ((caps as any).resizeMode?.includes('none')) {
          (hi as any).resizeMode = 'none';
        }
  
        console.log(hi);
        await track.applyConstraints(hi);
      } catch { }  */

    // attach preview
    const vid = this.videoRef.nativeElement;
    vid.srcObject = this.stream;
    const track = this.stream.getVideoTracks()[0];

  /*   const ImageCaptureClass = (window as any).ImageCapture;
    if (!ImageCaptureClass) {
      console.error('ImageCapture API not supported');
      return;
    }
    this.imageCapture = new ImageCaptureClass(track); */

    this.resizeVideoToPortrait1080x1440();

    await vid.play();
    this.cameraLoading = false;

    // WebKit-PWA freeze detector
    setTimeout(() => {
      const t = this.stream?.getVideoTracks()[0];
      this.showFreezeHint = (isIOS && t?.muted) ?? false;
    }, 1000);
  }

  private stop() { this.stream?.getTracks().forEach(t => t.stop()); }

  captureFrame() { this.showFrame ?  this.captureCrop() :  this.captureFull(); }


  /** Grab a full-res still from the video and emit as a data-URL */
async captureFull() {
  try {
    const videoEl = this.videoRef.nativeElement as HTMLVideoElement;
    const width  = videoEl.videoWidth;
    const height = videoEl.videoHeight;

    // draw the full-resolution frame into a canvas
    const canvas = document.createElement('canvas');
    canvas.width  = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(videoEl, 0, 0, width, height);

    // get a Blob (or DataURL) out of it
    canvas.toBlob(blob => {
      if (!blob) {
        console.error('could not capture frame');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => this.onCapture.emit(reader.result as string);
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.9);
  } catch (err) {
    console.error('captureFull failed:', err);
  }
}

/** Grab a full-res still, crop center 80×50%, and emit as a data-URL */
async captureCrop() {
  try {
    const videoEl = this.videoRef.nativeElement as HTMLVideoElement;
    const videoRect = videoEl.getBoundingClientRect();
    const cropRect  = this.cropBoxRef.nativeElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // 1) draw the displayed video into a temp canvas at CSS size×dpr
    const tmp = document.createElement('canvas');
    tmp.width  = videoRect.width  * dpr;
    tmp.height = videoRect.height * dpr;
    const tctx = tmp.getContext('2d')!;
    tctx.scale(dpr, dpr);
    tctx.drawImage(videoEl, 0, 0, videoRect.width, videoRect.height);

    // 2) figure out the crop in device-px
    const sx = (cropRect.left   - videoRect.left)   * dpr;
    const sy = (cropRect.top    - videoRect.top)    * dpr;
    const sw =  cropRect.width  * dpr;
    const sh =  cropRect.height * dpr;

    // 3) draw that slice into an output canvas
    const out = document.createElement('canvas');
    out.width  = sw;
    out.height = sh;
    const octx = out.getContext('2d')!;
    octx.drawImage(tmp, sx, sy, sw, sh, 0, 0, sw, sh);

    // 4) emit as JPEG DataURL
    const dataURL = out.toDataURL('image/jpeg', 0.9);
    this.onCapture.emit(dataURL);

  } catch (err) {
    console.error('captureCrop failed:', err);
  }
}


  /* async captureFull() {
    try {
      // hardware still at the video’s max resolution
      const blob = await this.imageCapture.takePhoto({
        imageWidth: this.videoRef.nativeElement.videoWidth,
        imageHeight: this.videoRef.nativeElement.videoHeight
      });

      // convert blob → dataURL
      const dataURL = await new Promise<string>((res, rej) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result as string);
        reader.onerror = e => rej(e);
        reader.readAsDataURL(blob);
      });

      this.onCapture.emit(dataURL);
    } catch (err) {
      console.error('captureFull failed:', err);
    }
  }

  // Grab a full-res still, crop center 80×50%, and emit as a data-URL 
  async captureCrop() {
    // 1) grab the on-screen dimensions
    const videoEl = this.videoRef.nativeElement as HTMLVideoElement;
    const videoRect = videoEl.getBoundingClientRect();
    const cropRect  = this.cropBoxRef.nativeElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
  
    // 2) draw the _displayed_ video into a temp canvas at CSS size×dpr
    const tmp = document.createElement('canvas');
    tmp.width  = videoRect.width  * dpr;
    tmp.height = videoRect.height * dpr;
    const tctx = tmp.getContext('2d')!;
    // scale so 1 CSS-px = 1 device-px
    tctx.scale(dpr, dpr);
    // draw the stretched video exactly as you see it
    tctx.drawImage(
      videoEl,
      0, 0,
      videoRect.width,
      videoRect.height
    );
  
    // 3) compute the crop box in that same device-px space
    const sx = (cropRect.left   - videoRect.left)   * dpr;
    const sy = (cropRect.top    - videoRect.top)    * dpr;
    const sw =  cropRect.width  * dpr;
    const sh =  cropRect.height * dpr;
  
    // 4) pull out just that rectangle
    const out = document.createElement('canvas');
    out.width  = sw;
    out.height = sh;
    const octx = out.getContext('2d')!;
    octx.drawImage(tmp, sx, sy, sw, sh, 0, 0, sw, sh);
  
    // 5) emit your result
    const dataURL = out.toDataURL('image/jpeg', 0.9);
    this.onCapture.emit(dataURL);
  } */
}
